import { BusinessMoreDetails } from "../../interfaces/BusinessObject";

/** moreDetails key holding a business's custom product sections (string[], ordered). */
export const SECTIONS_KEY = "SECTIONS";

/** moreDetails key holding sub-sections per section ({ [sectionName]: string[] }). */
export const SUBSECTIONS_KEY = "SUBSECTIONS";

/** Max characters allowed in a section name (shared by every section editor). */
export const MAX_SECTION_NAME = 40;

/**
 * Reads `moreDetails.SECTIONS` safely, coercing to a `string[]`. Accepts either the
 * full `BusinessMoreDetails` bag or the raw value already pulled off it, so callers
 * that already have `business?.moreDetails?.SECTIONS` in hand don't need to re-wrap it.
 */
export function readSections(details: BusinessMoreDetails | undefined | null): string[];
export function readSections(raw: unknown): string[];
export function readSections(input: BusinessMoreDetails | unknown | undefined | null): string[] {
  const raw =
    input && typeof input === "object" && !Array.isArray(input)
      ? (input as BusinessMoreDetails)[SECTIONS_KEY]
      : input;
  return Array.isArray(raw) ? (raw.filter((s) => typeof s === "string") as string[]) : [];
}

/**
 * Canonical form used ONLY to compare/match section and sub-section names —
 * never to decide what gets stored. "-" and whitespace are treated as the
 * same separator (so "arya-rentals", "arya rentals" and "arya  -  rentals"
 * all collapse to "arya rentals"), but case is preserved: this is layered on
 * top of the existing case-sensitive rule ("Electronics" !== "electronics"),
 * not a replacement for it.
 */
function canonicalizeSectionName(name: string): string {
  return name.trim().replace(/[-\s]+/g, " ");
}

/**
 * True when two section (or sub-section) names refer to the same group once
 * "-" and " " are treated interchangeably. Case-sensitive otherwise. Scoped
 * to SECTIONS/SUBSECTIONS matching — do not reuse this for other attributes.
 */
export function sectionNamesMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (a == null || b == null) return false;
  return canonicalizeSectionName(a) === canonicalizeSectionName(b);
}

/**
 * Returns the entry in `names` that matches `name` under sectionNamesMatch,
 * if any — i.e. the actual stored spelling to group/display under, rather
 * than whatever variant a product's own detail happens to carry.
 */
export function findMatchingSectionName(
  names: string[] | undefined | null,
  name: string | null | undefined
): string | undefined {
  if (!names || name == null) return undefined;
  return names.find((candidate) => sectionNamesMatch(candidate, name));
}

/**
 * Display-only: renders "-" as a space for section/sub-section names shown to
 * the user. Never changes what's stored — callers keep saving/sending the
 * raw value exactly as typed. Do not use this for any attribute other than
 * SECTIONS/SUBSECTIONS.
 */
export function displaySectionName(name: string): string {
  return name.replace(/-/g, " ");
}

/**
 * The backend can't handle whitespace in a section/sub-section name, so this
 * is the ONE place a typed name gets converted before it's persisted: every
 * run of whitespace becomes a single "-", and any leading/trailing separator
 * left over from that is trimmed off. Pairs with `displaySectionName`, which
 * renders it back as a space for the user — so a curator typing "Arya
 * Rentals" ends up with "Arya-Rentals" on the wire and "Arya Rentals" on
 * screen. Scoped to SECTIONS/SUBSECTIONS — do not use for other attributes.
 */
export function sanitizeSectionNameForSave(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Reads the sub-sections for a specific section from `moreDetails.SUBSECTIONS`.
 * Returns an empty array when no sub-sections have been configured.
 */
export function readSubSections(sectionName: string, details: unknown): string[] {
  if (!details || typeof details !== "object" || Array.isArray(details)) return [];
  const raw = (details as Record<string, unknown>)[SUBSECTIONS_KEY];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const bag = raw as Record<string, unknown>;
  // Exact key first (the common case); fall back to a "-"/" "-insensitive match
  // so a section passed in as "Arya Rentals" still finds sub-sections filed
  // under the stored "Arya-Rentals" key, or vice versa.
  const key = sectionName in bag ? sectionName : findMatchingSectionName(Object.keys(bag), sectionName);
  const forSection = key !== undefined ? bag[key] : undefined;
  return Array.isArray(forSection)
    ? (forSection.filter((s) => typeof s === "string") as string[])
    : [];
}
